import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguageContext } from '../contexts/LanguageContext';
import BannerSlider from '../components/BannerSlider';

interface NewsItem {
  _id: string;
  slug: string;
  title: string;
  subtitle: string;
  content: string;
  imageUrl: string;
  publishedAt: string;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const News: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage: language } = useLanguageContext();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNews(1, true);
  }, [language]);

  const fetchNews = async (page: number, reset: boolean = false) => {
    try {
      if (reset) {
      setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await fetch(`/api/news?page=${page}&limit=12&language=${language}`);
      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }
      const data = await response.json();
      
      if (reset) {
      setNews(data.news || []);
      } else {
        setNews(prevNews => [...prevNews, ...(data.news || [])]);
      }
      
      setPagination(data.pagination || { page: 1, limit: 12, total: 0, pages: 0 });
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = pagination.page + 1;
    if (nextPage <= pagination.pages) {
      fetchNews(nextPage, false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };



  const hasMoreNews = pagination.page < pagination.pages;

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Banner Slider - Full Width */}
      <div className="-mx-4">
        <BannerSlider />
      </div>
      
      <div className="container mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">
            {t('news.all_news')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('news.stay_informed')}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="h-80 bg-gray-700"></div>
                  <div className="p-8">
                    <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="h-6 bg-gray-700 rounded mb-4"></div>
                    <div className="h-4 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-16">
            <div className="text-red-400 text-xl mb-4">{error}</div>
            <button 
              onClick={() => fetchNews(1, true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
            >
              {t('common.try_again')}
            </button>
          </div>
        )}

        {/* News Grid */}
        {!isLoading && !error && news.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {news.map((item) => (
                <article key={item._id} className="bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors duration-200 flex flex-col h-full">
                  <div className="h-80 overflow-hidden">
                    <Link to={`/news/${item.slug}`} className="block h-full">
                                              <img
                          src={item.imageUrl}
                          alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/new_logo.png';
                        }}
                      />
                    </Link>
                  </div>
                  
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="text-sm text-purple-400 mb-4">
                      {formatDate(item.publishedAt)}
                    </div>
                    
                    <h2 className="text-2xl font-bold mb-4 text-white hover:text-purple-400 transition-colors duration-200 min-h-[4rem] line-clamp-2">
                      <Link to={`/news/${item.slug}`}>
                        {item.title.replace(/\\"/g, '"')}
                      </Link>
                    </h2>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed text-base flex-grow line-clamp-4">
                      {item.subtitle.replace(/\\"/g, '"')}
                    </p>
                    
                    <Link
                      to={`/news/${item.slug}`}
                      className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-200 font-medium mt-auto"
                    >
                      {t('news.read_more')}
                      <svg 
                        className="ml-2 w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M9 5l7 7-7 7" 
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {/* Load More Button */}
            {hasMoreNews && (
              <div className="text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className={`px-8 py-4 rounded-lg font-semibold transition-all duration-200 ${
                    isLoadingMore
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 transform'
                  }`}
                >
                  {isLoadingMore ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t('common.loading')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>{language === 'ru' ? 'Загрузить еще' : 'Load More'}</span>
                      <svg 
                        className="w-5 h-5" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                        />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            )}

            {/* No More News Indicator */}
            {!hasMoreNews && news.length > 0 && (
              <div className="text-center text-gray-400">
                <div className="inline-flex items-center space-x-2">
                  <div className="h-px bg-gray-600 w-16"></div>
                  <span className="text-sm">
                    {language === 'ru' ? 'Больше новостей нет' : 'No more news'}
                  </span>
                  <div className="h-px bg-gray-600 w-16"></div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!isLoading && !error && news.length === 0 && (
          <div className="text-center py-16">
            <div className="text-gray-400 text-xl mb-4">
              {t('news.no_news_available')}
            </div>
            <p className="text-gray-500">
              {t('news.check_back_later')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default News; 